import React, { useEffect, useState } from 'react';
import HamburgerMenu from './HamburgerMenu';

const LEGION_ORIGIN = 'https://platform-legion.amentum.com';
const ASSISTANT_ID = 'c36de401-7f07-4058-b9c3-7fe55c074d41';
const PROBE_TIMEOUT_MS = 2500;

let reachabilityCache = null;

export function resetLegionReachabilityCache() {
    reachabilityCache = null;
}

function legionWidgetSrc() {
    const params = new URLSearchParams({
        assistantId: ASSISTANT_ID,
        NEXT_PUBLIC_APPLICATION_URL: LEGION_ORIGIN,
    });
    return `${LEGION_ORIGIN}/ChatWidget.html?${params.toString()}`;
}

function probeAbortSignal(timeoutMs) {
    if (typeof AbortSignal.timeout === 'function') {
        return AbortSignal.timeout(timeoutMs);
    }
    const controller = new AbortController();
    setTimeout(() => controller.abort(), timeoutMs);
    return controller.signal;
}

async function probeLegion() {
    if (reachabilityCache !== null) {
        return reachabilityCache;
    }
    try {
        await fetch(`${LEGION_ORIGIN}/`, {
            mode: 'no-cors',
            signal: probeAbortSignal(PROBE_TIMEOUT_MS),
        });
        reachabilityCache = true;
    } catch {
        reachabilityCache = false;
    }
    return reachabilityCache;
}

const getStyles = (theme) => {
    const isDark = theme === 'dark';
    return {
        page: {
            minHeight: '100vh',
            backgroundColor: isDark ? '#121212' : '#f5f7fb',
            color: isDark ? '#ececec' : '#1f2937',
            padding: '20px 0 30px',
        },
        container: {
            width: '92%',
            maxWidth: '1450px',
            margin: '0 auto',
            fontFamily: 'Inter, system-ui, sans-serif',
        },
        header: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '22px',
        },
        titleWrap: {
            flexGrow: 1,
            textAlign: 'center',
        },
        title: {
            margin: 0,
            fontSize: '1.8rem',
            letterSpacing: '0.2px',
        },
        subtitle: {
            margin: '4px 0 0',
            color: isDark ? '#b3b9c4' : '#5f6b7a',
            fontSize: '0.95rem',
        },
        frame: {
            width: '100%',
            height: '700px',
            border: 'none',
            backgroundColor: isDark ? '#1e1e1e' : '#ffffff',
        },
        message: {
            margin: '24px 0 0',
            color: isDark ? '#b3b9c4' : '#5f6b7a',
            fontSize: '1rem',
        },
    };
};

function LegionPage({ theme = 'light' }) {
    const styles = getStyles(theme);
    const [status, setStatus] = useState(reachabilityCache === null ? 'checking' : (reachabilityCache ? 'available' : 'unavailable'));

    useEffect(() => {
        let cancelled = false;
        probeLegion().then((reachable) => {
            if (!cancelled) {
                setStatus(reachable ? 'available' : 'unavailable');
            }
        });
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <div style={styles.page}>
            <div style={styles.container}>
                <div style={styles.header}>
                    <HamburgerMenu theme={theme} />
                    <div style={styles.titleWrap}>
                        <h1 style={styles.title}>Legion</h1>
                        <p style={styles.subtitle}>Company network assistant</p>
                    </div>
                </div>
                {status === 'available' ? (
                    <iframe
                        title="Legion assistant"
                        src={legionWidgetSrc()}
                        width="100%"
                        height="700"
                        style={styles.frame}
                        allow="clipboard-write"
                    />
                ) : null}
                {status === 'unavailable' ? (
                    <p style={styles.message}>Legion is only available on the company network.</p>
                ) : null}
            </div>
        </div>
    );
}

export default LegionPage;
